import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { token } from '@bp-files/ui';

/**
 * The "⋯" overflow — this surface's copy of `FolderView`'s `RowActions`.
 *
 * **It lived inside `SpaceList.tsx` and moved here when its one act left.**
 * CON-14's remove and leave sit beside the agency they name now, which is a row
 * of the members panel rather than a row of the space table — so the menu had
 * two consumers in two files and no longer belonged to either.
 *
 * It takes no copy of its own: the trigger's accessible name and the items are
 * the caller's, from `spaceList.copy.ts`, which is what keeps this file a
 * mechanism rather than a fourth place a string can live.
 *
 * **The panel renders through a portal, into `document.body`** — `RowActions`'
 * precedent, and for the same two reasons. First, `DataTable` wraps every table
 * in `overflow-x-auto`, and per the CSS overflow spec that forces the used
 * `overflow-y` to `auto` too, which clipped an `absolute` panel at the table's
 * bottom edge. Second, this surface sits inside `SurfaceHeader`'s
 * `bp-animate-fade-in` wrapper, which animates `opacity` from 0 — an element
 * under `opacity: 1` creates a stacking context for as long as that animation
 * runs, so the panel's `z-10` was only 10 *within* that wrapper, not against the
 * page. A row opened while the surface was still fading in painted its menu
 * *under* `AppShell`'s `z-20` header, the same trap `DetailDrawer` was portalled
 * to `document.body` to escape. `position: fixed` computed from the trigger's own
 * `getBoundingClientRect()` escapes both the clipping ancestor and the stacking
 * one — and it is what makes the menu work inside `DetailDrawer` too, which is
 * its own stacking context at `z-40`.
 *
 * **Confirmation lives inside this menu.** A click on `RowActionsItem` with
 * `keepOpen` set (`SpaceList`'s "Delete this space") stops the click bubbling
 * to the panel's own close-on-select handler, so the caller can swap the
 * panel's children to `RowActionsConfirm` in place — a click on either of
 * *those* buttons has no such guard, so a decision either way closes the menu
 * on the ordinary bubble. This used to be the opposite rule — confirmation
 * rendered as the row's own inline pair, outside the menu entirely, on
 * `MemberRowActions`' precedent — until both surfaces moved it in here so a
 * row offers exactly one control regardless of state.
 *
 * Owns its own open state rather than lifting it: nothing else needs to know
 * which row's menu is open, and every row gets an independent instance for free.
 * Closes on an outside click — checked against both the trigger's wrapper and
 * the portalled panel, since the panel is no longer a DOM descendant of the
 * former — on Escape, on scroll or resize (the position was computed once at
 * open time and does not track the trigger live), and (via the panel's own
 * `onClick`, which every item click bubbles into) on selecting anything inside
 * it.
 */
const ROW_ACTIONS_GAP = 4;
// One trigger, up to four items behind it — roomy enough that the estimate
// never has to be exact, on `MemberRowActions`' precedent: the menu closes on
// scroll or resize rather than tracking its position live.
const ROW_ACTIONS_HEIGHT = 200;

export function RowActionsMenu({
  label,
  glyph,
  disabled = false,
  children,
}: {
  /** The trigger's accessible name. Named per row by the caller. */
  label: string;
  /** The visible mark. `aria-hidden` — `label` carries the whole name. */
  glyph: string;
  /**
   * Holds the trigger inert while this row has a request in flight. Every act
   * behind it now closes the panel on the ordinary click bubble before that
   * request settles — this is what stops a second click from reopening the
   * menu mid-flight, since there is no longer a row-level button to disable
   * instead.
   */
  disabled?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{
    right: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  /*
    **Focus moves into the menu, and back to the trigger when it closes.**
    `RowActions` and `MemberRowActions` do neither, and neither needs to: they
    sit in a table, where a portalled panel is still the next thing in the
    document's tab order. This one opens inside `DetailDrawer`, whose focus trap
    wraps Tab at the *panel's* last focusable element — and the portal is not a
    descendant of that panel, so without this the items are reachable by mouse
    and by nothing else. Escape and an outside click both land here too, because
    both set `open` false.
  */
  useEffect(() => {
    if (!open) return;
    // Captured on the way in rather than read in the cleanup: by the time an
    // effect's teardown runs, a ref can point somewhere else — here it can point
    // at nothing at all, because selecting a destructive item swaps the trigger
    // for the row's Confirm/Cancel pair.
    const trigger = triggerRef.current;
    panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => {
      trigger?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node;
      const insideTrigger = rootRef.current?.contains(target) ?? false;
      const insidePanel = panelRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insidePanel) setOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    // Closes rather than re-tracks, on `RowActions`' reasoning: a menu whose
    // position goes stale mid-scroll is a worse bug than one that closes a
    // beat sooner than it strictly had to. `capture: true` so a scroll inside
    // `DataTable`'s own scrollport — the ancestor this portal exists to
    // escape — closes the menu too, not only a scroll of the page itself.
    function close() {
      setOpen(false);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', close, { capture: true });
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', close, { capture: true });
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={() => {
          setOpen((current) => {
            const next = !current;
            if (next) {
              const rect = triggerRef.current?.getBoundingClientRect();
              if (rect) {
                const right = window.innerWidth - rect.right;
                // A row can sit against the visible viewport's lower edge;
                // open into the available space above it instead of
                // clipping the actions below the screen.
                const opensAbove =
                  rect.bottom + ROW_ACTIONS_GAP + ROW_ACTIONS_HEIGHT > window.innerHeight;

                setPosition(
                  opensAbove
                    ? {
                        bottom: Math.max(
                          ROW_ACTIONS_GAP,
                          window.innerHeight - rect.top + ROW_ACTIONS_GAP,
                        ),
                        right,
                      }
                    : { top: rect.bottom + ROW_ACTIONS_GAP, right },
                );
              } else {
                setPosition(null);
              }
            }
            return next;
          });
        }}
        className="flex h-9 w-9 items-center justify-center border text-base transition-colors hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
        style={{
          borderColor: token('bp-border-strong'),
          color: token('bp-text'),
          borderRadius: token('radius-md'),
        }}
      >
        <span aria-hidden="true">{glyph}</span>
      </button>

      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              aria-label={label}
              onClick={() => {
                setOpen(false);
              }}
              // `z-50`, above `DetailDrawer`'s own `z-40`: this menu opens
              // inside that drawer on the members panel, and a portal to
              // `document.body` sits beside the drawer rather than inside it,
              // so painting order is all that separates them.
              className="fixed z-50 min-w-max border py-1"
              style={{
                top: position.top,
                bottom: position.bottom,
                right: position.right,
                background: token('bp-panel'),
                borderColor: token('bp-border-strong'),
                borderRadius: token('radius-md'),
                boxShadow: token('shadow-md'),
              }}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/**
 * One item in the menu above. A `<button role="menuitem">`, so the panel's own
 * `onClick` closes the menu when it bubbles.
 */
export function RowActionsItem({
  label,
  tone,
  onSelect,
  keepOpen,
}: {
  label: string;
  /** `danger` for an act that takes something away. */
  tone?: 'danger';
  onSelect: () => void;
  /**
   * Stops the click bubbling to the panel's own close-on-select handler, so
   * this selection can swap the panel's content — to a `RowActionsConfirm`
   * step, typically — instead of closing it. Selecting a destructive item is
   * the one case this menu uses it for; everything else closes on select.
   */
  keepOpen?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={(event) => {
        if (keepOpen) event.stopPropagation();
        onSelect();
      }}
      className="block w-full whitespace-nowrap px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-elevated"
      style={{ color: tone === 'danger' ? token('bp-danger') : token('bp-text') }}
    >
      {label}
    </button>
  );
}

/**
 * The confirm/cancel step for a destructive `RowActionsItem`, rendered as the
 * panel's own content in its place — this menu's confirmation lives inside
 * the panel rather than replacing the row (see this file's header). Neither
 * button sets `keepOpen`: a decision, either way, closes the menu on the
 * ordinary bubble every other item here relies on.
 */
export function RowActionsConfirm({
  cancelLabel,
  confirmLabel,
  busy = false,
  onCancel,
  onConfirm,
}: {
  cancelLabel: string;
  confirmLabel: string;
  /** A delete is in flight. Confirm is held; cancel still works. */
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <>
      <button
        type="button"
        role="menuitem"
        onClick={onCancel}
        className="block w-full whitespace-nowrap px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-elevated"
        style={{ color: token('bp-text') }}
      >
        {cancelLabel}
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={busy}
        onClick={onConfirm}
        className="block w-full whitespace-nowrap px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
        style={{ color: token('bp-danger') }}
      >
        {confirmLabel}
      </button>
    </>
  );
}
